import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Tag, Tooltip } from 'antd'
import { CloseOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons'

/**
 * Строка фильтра в стиле GitLab (filtered search):
 * - клик по строке открывает список ключей фильтра («Модуль», «Подключение»…);
 * - выбор ключа → список значений, выбранное значение превращается в чип «Ключ | Значение ×»;
 * - свободный текст + Enter → чип текстового поиска;
 * - Backspace в пустом поле удаляет последний чип, Esc закрывает список, ↑/↓ — навигация.
 */

export type FilterOption = {
  value: string
  label: string
  /** Число справа (например, количество коллекций). */
  count?: number
  /** Подпись-подсказка справа (например, «не подключён»). */
  hint?: string
}

export type FilterKeyDef = {
  key: string
  title: string
  /** Можно выбрать несколько значений (объединяются по ИЛИ). */
  multiple?: boolean
  options: FilterOption[]
}

export type FilterToken = { key: string; value: string }

export type FilteredSearchValue = {
  tokens: FilterToken[]
  search: string
}

type Suggestion =
  | { type: 'search'; text: string }
  | { type: 'key'; def: FilterKeyDef }
  | { type: 'value'; def: FilterKeyDef; option: FilterOption }

interface IProps {
  filterKeys: FilterKeyDef[]
  value: FilteredSearchValue
  onChange: (value: FilteredSearchValue) => void
  placeholder?: string
  disabled?: boolean
}

const styles = {
  wrapper: { position: 'relative' as const, flex: 1, minWidth: 280 },
  bar: (focused: boolean, disabled?: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 4,
    minHeight: 34,
    padding: '2px 4px 2px 8px',
    border: `1px solid ${focused ? '#1677ff' : '#d9d9d9'}`,
    boxShadow: focused ? '0 0 0 2px rgba(5, 145, 255, 0.1)' : 'none',
    borderRadius: 6,
    background: disabled ? '#f5f5f5' : '#fff',
    cursor: disabled ? 'not-allowed' : 'text',
  }),
  input: {
    flex: 1,
    minWidth: 120,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '4px 2px',
    fontSize: 14,
  },
  tokenKey: {
    padding: '1px 6px',
    background: '#e6e6e6',
    borderRadius: '4px 0 0 4px',
    color: '#333',
    fontSize: 12,
  },
  tokenValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 6px',
    background: '#e6f4ff',
    borderRadius: '0 4px 4px 0',
    color: '#0958d9',
    fontSize: 12,
  },
  tokenText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 6px',
    background: '#f0f0f0',
    borderRadius: 4,
    fontSize: 12,
  },
  dropdown: {
    position: 'absolute' as const,
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 1050,
    minWidth: 300,
    maxWidth: 420,
    maxHeight: 340,
    overflowY: 'auto' as const,
    background: '#fff',
    borderRadius: 6,
    boxShadow:
      '0 6px 16px 0 rgba(0,0,0,0.08), 0 3px 6px -4px rgba(0,0,0,0.12), 0 9px 28px 8px rgba(0,0,0,0.05)',
    padding: 4,
  },
  dropdownHeader: { padding: '4px 8px', color: '#8c8c8c', fontSize: 12 },
  item: (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '6px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    background: active ? '#f0f5ff' : 'transparent',
  }),
}

const FilteredSearch: React.FC<IProps> = ({
  filterKeys,
  value,
  onChange,
  placeholder = 'Поиск или фильтр…',
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [pendingKey, setPendingKey] = useState<FilterKeyDef | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const keyByName = useMemo(() => new Map(filterKeys.map(def => [def.key, def])), [filterKeys])

  const labelFor = useCallback(
    (token: FilterToken) => {
      const def = keyByName.get(token.key)
      const option = def?.options.find(o => o.value === token.value)
      return { keyTitle: def?.title || token.key, valueLabel: option?.label || token.value }
    },
    [keyByName],
  )

  const suggestions: Suggestion[] = useMemo(() => {
    const q = text.trim().toLowerCase()
    if (pendingKey) {
      const used = new Set(value.tokens.filter(t => t.key === pendingKey.key).map(t => t.value))
      return pendingKey.options
        .filter(o => !used.has(o.value))
        .filter(o => !q || o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
        .map(option => ({ type: 'value' as const, def: pendingKey, option }))
    }
    const keys = filterKeys
      .filter(def => def.multiple || !value.tokens.some(t => t.key === def.key))
      .filter(def => !q || def.title.toLowerCase().startsWith(q) || def.key.startsWith(q))
      .map(def => ({ type: 'key' as const, def }))
    return q ? [{ type: 'search' as const, text: text.trim() }, ...keys] : keys
  }, [text, pendingKey, filterKeys, value.tokens])

  useEffect(() => {
    setActiveIndex(0)
  }, [text, pendingKey])

  const focusInput = () => {
    if (disabled) return
    inputRef.current?.focus()
    setOpen(true)
  }

  const emit = (next: Partial<FilteredSearchValue>) => {
    onChange({ tokens: value.tokens, search: value.search, ...next })
  }

  const select = (s: Suggestion) => {
    if (s.type === 'search') {
      emit({ search: s.text })
      setText('')
      setOpen(false)
      return
    }
    if (s.type === 'key') {
      setPendingKey(s.def)
      setText('')
      setOpen(true)
      return
    }
    const tokens = s.def.multiple
      ? [...value.tokens, { key: s.def.key, value: s.option.value }]
      : [
          ...value.tokens.filter(t => t.key !== s.def.key),
          { key: s.def.key, value: s.option.value },
        ]
    setPendingKey(null)
    setText('')
    emit({ tokens })
    setOpen(true)
  }

  const removeToken = (index: number) => {
    emit({ tokens: value.tokens.filter((_, i) => i !== index) })
  }

  const clearAll = () => {
    setPendingKey(null)
    setText('')
    onChange({ tokens: [], search: '' })
  }

  const onInputChange = (raw: string) => {
    // «модуль:» / «module:» — сразу переходим к выбору значения, как в GitLab
    if (!pendingKey && raw.endsWith(':')) {
      const name = raw.slice(0, -1).trim().toLowerCase()
      const def = filterKeys.find(d => d.title.toLowerCase() === name || d.key === name)
      if (def) {
        setPendingKey(def)
        setText('')
        return
      }
    }
    setText(raw)
    setOpen(true)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex(i => Math.min(i + 1, Math.max(suggestions.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const s = suggestions[activeIndex]
      if (open && s) {
        select(s)
      } else if (!pendingKey) {
        emit({ search: text.trim() })
        setText('')
        setOpen(false)
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setPendingKey(null)
    } else if (e.key === 'Backspace' && !text) {
      if (pendingKey) {
        setPendingKey(null)
      } else if (value.search) {
        emit({ search: '' })
      } else if (value.tokens.length) {
        removeToken(value.tokens.length - 1)
      }
    }
  }

  const hasAny = value.tokens.length > 0 || Boolean(value.search) || Boolean(text) || !!pendingKey

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flex: 1 }}>
      <div style={styles.wrapper}>
        <div style={styles.bar(focused, disabled)} onClick={focusInput}>
          <FilterOutlined style={{ color: '#8c8c8c', marginRight: 2 }} />
          {value.tokens.map((token, index) => {
            const { keyTitle, valueLabel } = labelFor(token)
            return (
              <span key={`${token.key}:${token.value}`} style={{ display: 'inline-flex' }}>
                <span style={styles.tokenKey}>{keyTitle}</span>
                <span style={styles.tokenValue}>
                  {valueLabel}
                  {!disabled && (
                    <CloseOutlined
                      style={{ fontSize: 10, cursor: 'pointer' }}
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => {
                        e.stopPropagation()
                        removeToken(index)
                      }}
                    />
                  )}
                </span>
              </span>
            )
          })}
          {value.search && (
            <span style={styles.tokenText}>
              <SearchOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
              {value.search}
              {!disabled && (
                <CloseOutlined
                  style={{ fontSize: 10, cursor: 'pointer' }}
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => {
                    e.stopPropagation()
                    emit({ search: '' })
                  }}
                />
              )}
            </span>
          )}
          {pendingKey && (
            <span style={{ ...styles.tokenKey, borderRadius: 4 }}>{pendingKey.title}</span>
          )}
          <input
            ref={inputRef}
            style={styles.input}
            value={text}
            disabled={disabled}
            placeholder={value.tokens.length || value.search || pendingKey ? '' : placeholder}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => {
              setFocused(true)
              setOpen(true)
            }}
            onBlur={() => {
              setFocused(false)
              setOpen(false)
            }}
          />
          {hasAny && !disabled && (
            <Tooltip title="Сбросить фильтр">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onMouseDown={e => e.preventDefault()}
                onClick={e => {
                  e.stopPropagation()
                  clearAll()
                }}
              />
            </Tooltip>
          )}
        </div>
        {open && !disabled && suggestions.length > 0 && (
          <div style={styles.dropdown} onMouseDown={e => e.preventDefault()}>
            <div style={styles.dropdownHeader}>
              {pendingKey ? `${pendingKey.title}: выберите значение` : 'Фильтр по'}
            </div>
            {suggestions.map((s, index) => {
              const active = index === activeIndex
              const itemKey =
                s.type === 'search'
                  ? 'search'
                  : s.type === 'key'
                  ? `key:${s.def.key}`
                  : `val:${s.option.value}`
              const common = {
                style: styles.item(active),
                onMouseEnter: () => setActiveIndex(index),
                onClick: () => select(s),
              }
              if (s.type === 'search') {
                return (
                  <div key={itemKey} {...common}>
                    <span>
                      <SearchOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
                      Искать «{s.text}»
                    </span>
                    <span style={{ color: '#bfbfbf', fontSize: 12 }}>Enter</span>
                  </div>
                )
              }
              if (s.type === 'key') {
                return (
                  <div key={itemKey} {...common}>
                    <span>{s.def.title}</span>
                    <span style={{ color: '#bfbfbf', fontSize: 12 }}>
                      {s.def.multiple ? 'одно из' : '='}
                    </span>
                  </div>
                )
              }
              return (
                <div key={itemKey} {...common}>
                  <span>{s.option.label}</span>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    {s.option.hint && (
                      <Tag style={{ marginInlineEnd: 0 }} color="default">
                        {s.option.hint}
                      </Tag>
                    )}
                    {typeof s.option.count === 'number' && (
                      <span style={{ color: '#8c8c8c', fontSize: 12 }}>{s.option.count}</span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <Button
        icon={<SearchOutlined />}
        disabled={disabled}
        onClick={() => {
          if (text.trim() && !pendingKey) {
            emit({ search: text.trim() })
            setText('')
          } else {
            emit({})
          }
        }}
      />
    </div>
  )
}

export default FilteredSearch
